import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
	fetchDrugCategories,
	fetchAllDrugs,
} from '../../redux/middleware/medication/drugs/drugs';
import { currencyMapping } from '../../utils/currencyData';
import {
	Typography,
	Box,
	Card,
	CardContent,
	CardMedia,
	Skeleton,
	Grid,
	Button,
	TextField,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	Pagination,
	Alert,
	Rating,
	useTheme,
	Toolbar,
	InputAdornment,
} from '@mui/material';
import { shades } from '../../../theme';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';

import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import useMediaQuery from '@mui/material/useMediaQuery';
import doctorImage from '../../data/about/m-treat-doctor.jpg';
import { addToCart } from '../../redux/reducers/cartSlice';

const Listings = ({ drug }) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';
	const [selected, setSelected] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const [priceRange, setPriceRange] = useState('');
	const isXs = useMediaQuery((theme) => theme.breakpoints.down('sm'));
	const isSm = useMediaQuery((theme) => theme.breakpoints.between('sm', 'md'));
	const perPage = isXs ? 10 : isSm ? 18 : 32;

	const items = ['DRUGS', 'EQUIPMENTS', 'SERVICES', 'GIFTS'];
	const pricePoints = [50, 100, 200];
	const dispatch = useDispatch();
	const [userLocation, setUserLocation] = useState(null);
	const [maxDistance, setMaxDistance] = useState(0);
	const [supplierLocations, setSupplierLocations] = useState({});
	const [drugCategories, setDrugCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState('all');
	const { drugs } = useSelector((state) => state.drugs);
	const [isSessionClearing, setIsSessionClearing] = useState(false);
	const [convertedPrices, setConvertedPrices] = useState({});
	const [conversionLoading, setConversionLoading] = useState({});
	const cart = useSelector((state) => state.cart.cart);
	const cartError = useSelector((state) => state.cart.error);
	const [page, setPage] = useState(1);
	const itemsPerPage = 8;
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const hasFetched = useRef(false);

	// Add location fetching functions
	const fetchUserLocation = async () => {
		try {
			const response = await axios.get('/medication/users/location/');
			setUserLocation({
				latitude: response.data.latitude,
				longitude: response.data.longitude,
			});
		} catch (error) {
			console.error('Error fetching user location:', error);
		}
	};

	const fetchSupplierLocations = async () => {
		try {
			const response = await axios.get('medication/supplier-locations/');
			setSupplierLocations(response.data);
		} catch (error) {
			console.error('Error fetching supplier locations:', error);
		}
	};

	// Add useEffect for fetching locations
	useEffect(() => {
		fetchUserLocation();
		fetchSupplierLocations();
	}, []);

	// Fetch drug categories
	useEffect(() => {
		fetchCategories();
		return () => {
			setDrugCategories([]);
			setLoading(false);
			setError(null);
		};
	}, []);

	const fetchCategories = async () => {
		setLoading(true);
		setError(null);
		try {
			const categories = await dispatch(fetchDrugCategories());
			const sortedCategories = categories
				.slice()
				.sort((a, b) => a.name.localeCompare(b.name));
			setDrugCategories(sortedCategories);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	// Fetch all drugs
	useEffect(() => {
		if (!hasFetched.current) {
			dispatch(fetchAllDrugs());
			hasFetched.current = true;
		}
	}, [dispatch]);

	// Add clear session function
	const clearCurrencySession = async () => {
		setIsSessionClearing(true);
		try {
			const response = await fetch('/medication/drug/clear_currency_session/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
			});

			if (response.ok) {
				// Reset converted prices after clearing session
				setConvertedPrices({});
				// Trigger new conversions
				drugs.forEach((drug) => {
					convertDrugPrice(drug.id, drug.price, drug.currency);
				});
			}
		} catch (error) {
			console.error('Failed to clear currency session:', error);
		} finally {
			setIsSessionClearing(false);
		}
	};

	// Add useEffect to clear session on mount
	useEffect(() => {
		clearCurrencySession();
	}, []);

	// Add conversion function
	const convertDrugPrice = async (drugId, price, currency) => {
		if (!price || !currency) return;

		setConversionLoading((prev) => ({ ...prev, [drugId]: true }));
		try {
			const response = await fetch('/medication/drug/convert_currency/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					price,
					currency,
				}),
			});
			const data = await response.json();
			if (data.converted_price) {
				setConvertedPrices((prev) => ({
					...prev,
					[drugId]: {
						price: data.converted_price,
						currency: data.user_currency,
						symbol: getCurrencySymbol(data.user_currency),
					},
				}));
			}
		} catch (error) {
			console.error('Currency conversion failed:', error);
		} finally {
			setConversionLoading((prev) => ({ ...prev, [drugId]: false }));
		}
	};

	useEffect(() => {
		if (drug?.price_info) {
			convertDrugPrice(
				drug.id,
				drug.price_info.price,
				drug.price_info.currency,
			);
		}
	}, [drug?.id, drug?.price_info?.currency]);

	useEffect(() => {
		pricePoints.forEach((price) => {
			convertDrugPrice(`price_${price}`, price, 'USD');
		});
	}, []);

	// currency symbol function
	const getCurrencySymbol = (currencyCode) => {
		// Find the country that uses this currency code
		const country = Object.entries(currencyMapping).find(
			([_, data]) => data.code === currencyCode,
		);
		// Return the symbol if found, otherwise return the currency code
		return country ? country[1].symbol : currencyCode;
	};

	const formatPrice = (price, currency) => {
		const symbol = getCurrencySymbol(currency);
		return `${symbol}${price}`;
	};

	// Filter drugs
	const filteredDrugs = drugs.filter((drug) => {
		// Basic filters
		const nameMatch = drug.name
			.toLowerCase()
			.includes(searchQuery.toLowerCase());
		const priceMatch = priceRange ? drug.price <= priceRange : true;
		const categoryMatch =
			selectedCategory === 'all'
				? true
				: drug.categories.some((cat) => cat.id === selectedCategory);

		// Distance filter
		const supplierLocation = supplierLocations[drug.user_id];
		let distanceMatch = true;

		if (userLocation && supplierLocation && maxDistance > 0) {
			const distance = calculateDistance(
				userLocation.latitude,
				userLocation.longitude,
				supplierLocation.latitude,
				supplierLocation.longitude,
			);
			distanceMatch = distance <= maxDistance;
		}

		return nameMatch && priceMatch && categoryMatch && distanceMatch;
	});

	const handleSearchChange = (event) => setSearchQuery(event.target.value);
	const handlePriceRangeChange = (event) => setPriceRange(event.target.value);
	const handleCategoryChange = (event) =>
		setSelectedCategory(event.target.value);
	const handleDistanceChange = (event) =>
		setMaxDistance(Number(event.target.value));
	const handlePageChange = (event, value) => setPage(value);

	const paginatedDrugs = filteredDrugs.slice(
		(page - 1) * itemsPerPage,
		page * itemsPerPage,
	);

	// Add to cart
	const handleAddToCart = (drug) => {
		dispatch(
			addToCart({
				item: {
					id: drug.id,
					name: drug.name,
					price: drug.price,
					currency: drug.currency,
					img: drug.img || defaultImage,
					stock: drug.stock,
					count: 1,
				},
				message: `${drug.name} added to cart`,
			}),
		);
	};

	// Helper function to calculate available stock
	const getAvailableStock = (drug) => {
		const cartItem = cart.find((item) => item.id === drug.id);
		const inCart = cartItem ? cartItem.count : 0;
		return drug.stock - inCart;
	};

	if (error) return <Typography color="error">Error: {error}</Typography>;

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				backgroundColor: isDarkMode ? shades.secondary[800] : 'white',
				color: isDarkMode ? shades.white[100] : 'black',
				transition: 'background-color 0.3s ease, color 0.3s ease',
			}}
		>
			<Box
				sx={{
					width: { xs: '95%', sm: '95%', md: '80%' },
					paddingTop: 3,
					pb: 3,
				}}
			>
				{/* List Type Selector */}
				<Box sx={{ textAlign: 'center' }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: { xs: 0.5, sm: 1, md: 2 },
						}}
					>
						{items.map((label, index) => (
							<Box
								key={label}
								onClick={() => setSelected(index)}
								sx={{
									cursor: 'pointer',
									textAlign: 'center',
									fontWeight: 'bold',
									fontSize: { xs: '0.6rem', sm: '0.9rem', md: '1.1rem' },
									borderRadius: 1,
									padding: { xs: '0.3rem', sm: '0.5rem', md: '0.7rem' },
									width: { xs: '18%', sm: '18%', md: '18%' },
									backgroundColor:
										selected === index
											? '#2783f7'
											: isDarkMode
												? shades.secondary[700]
												: 'white',
									color:
										selected === index
											? 'white'
											: isDarkMode
												? shades.white[100]
												: 'black',
									border:
										isDarkMode && selected !== index
											? '1px solid rgba(255,255,255,0.1)'
											: 'none',
									transition: 'all 0.3s',
									'&:hover': {
										backgroundColor: '#2783f7',
										color: 'white',
										border: 'none',
									},
								}}
							>
								{label}
							</Box>
						))}
					</Box>
				</Box>

				{/* General Box for the Filter Section */}
				{selected === 0 && (
					<Box>
						{/* Filter Section */}
						<Box>
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									flexWrap: 'nowrap', // Prevents wrapping to new lines
									justifyContent: 'space-between', // Distributes items evenly
									gap: 1,
									padding: { xs: 1, sm: 1.5, md: 2 },
									mt: 2,
									overflowX: 'auto',
								}}
							>
								{/* Search Filter */}
								<TextField
									label="Search"
									variant="outlined"
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder="Name"
									size="small"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon
													sx={{
														color: isDarkMode ? '#90caf9' : '#2783f7',
														fontSize: '1rem',
													}}
												/>
											</InputAdornment>
										),
										sx: {
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
											color: isDarkMode ? shades.white[100] : 'black',
										},
									}}
									sx={{
										minWidth: '20%',
										maxWidth: '25%',
										backgroundColor: isDarkMode
											? shades.secondary[700]
											: 'white',
										input: {
											color: isDarkMode ? shades.white[100] : 'black',
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
											'&::placeholder': {
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												opacity: 0.8,
												color: isDarkMode ? shades.white[500] : 'inherit',
											},
										},
										'& .MuiOutlinedInput-root': {
											'& fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.2)'
													: '#2783f7',
											},
											'&:hover fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.3)'
													: '#1976d2',
											},
											'&.Mui-focused fieldset': {
												borderColor: isDarkMode ? '#90caf9' : '#2783f7',
											},
										},
										'& .MuiInputLabel-root': {
											color: isDarkMode ? shades.white[200] : 'black',
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
										},
										'& .MuiInputLabel-root.Mui-focused': {
											color: isDarkMode ? '#90caf9' : '#2783f7',
										},
									}}
								/>

								{/* Price Filter */}
								<FormControl
									size="small"
									sx={{
										minWidth: '20%',
										maxWidth: '25%',
										backgroundColor: isDarkMode
											? shades.secondary[700]
											: 'white',
										'& .MuiOutlinedInput-root': {
											'& fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.2)'
													: '#2783f7',
											},
											'&:hover fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.3)'
													: '#1976d2',
											},
											'&.Mui-focused fieldset': {
												borderColor: isDarkMode ? '#90caf9' : '#2783f7',
											},
										},
									}}
								>
									<InputLabel
										sx={{
											color: isDarkMode ? shades.white[200] : 'black',
											'&.Mui-focused': {
												color: isDarkMode ? '#90caf9' : 'black',
											},
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
										}}
									>
										Price
									</InputLabel>
									<Select
										value={priceRange}
										onChange={handlePriceRangeChange}
										label="Price"
										sx={{
											color: isDarkMode ? shades.white[100] : 'black',
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
											'.MuiSelect-select': {
												padding: {
													xs: '6px 28px 6px 8px',
													sm: '8px 32px 8px 10px',
												},
											},
											'& .MuiSvgIcon-root': {
												color: isDarkMode ? shades.white[200] : 'inherit',
											},
										}}
										MenuProps={{
											PaperProps: {
												sx: {
													bgcolor: isDarkMode ? shades.secondary[700] : 'white',
												},
											},
										}}
									>
										<MenuItem
											value=""
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											<em>All</em>
										</MenuItem>
										{pricePoints.map((price) => (
											<MenuItem
												key={`price_${price}`}
												value={price}
												sx={{
													fontSize: {
														xs: '0.7rem',
														sm: '0.75rem',
														md: '0.8rem',
													},
													color: isDarkMode ? shades.white[100] : 'inherit',
													'&:hover': {
														backgroundColor: isDarkMode
															? 'rgba(255,255,255,0.08)'
															: 'rgba(0,0,0,0.04)',
													},
													'&.Mui-selected': {
														backgroundColor: isDarkMode
															? 'rgba(144, 202, 249, 0.16)'
															: 'rgba(33, 150, 243, 0.08)',
														'&:hover': {
															backgroundColor: isDarkMode
																? 'rgba(144, 202, 249, 0.24)'
																: 'rgba(33, 150, 243, 0.12)',
														},
													},
												}}
											>
												{conversionLoading[`price_${price}`]
													? 'Converting...'
													: convertedPrices[`price_${price}`]
														? `Under ${convertedPrices[`price_${price}`].symbol}${convertedPrices[`price_${price}`].price}`
														: `Under $${price}`}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{/* Category Filter */}
								<FormControl
									size="small"
									sx={{
										minWidth: '20%',
										maxWidth: '25%',
										backgroundColor: isDarkMode
											? shades.secondary[700]
											: 'white',
										'& .MuiOutlinedInput-root': {
											'& fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.2)'
													: '#2783f7',
											},
											'&:hover fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.3)'
													: '#1976d2',
											},
											'&.Mui-focused fieldset': {
												borderColor: isDarkMode ? '#90caf9' : '#2783f7',
											},
										},
									}}
								>
									<InputLabel
										sx={{
											color: isDarkMode ? shades.white[200] : 'black',
											'&.Mui-focused': {
												color: isDarkMode ? '#90caf9' : 'black',
											},
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
										}}
									>
										Category
									</InputLabel>
									<Select
										labelId="category-select-label"
										value={selectedCategory}
										onChange={handleCategoryChange}
										label="Category"
										sx={{
											color: isDarkMode ? shades.white[100] : '#2C3E50',
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
											'.MuiSelect-select': {
												padding: {
													xs: '6px 28px 6px 8px',
													sm: '8px 32px 8px 10px',
												},
											},
											'& .MuiSvgIcon-root': {
												color: isDarkMode ? shades.white[200] : 'inherit',
											},
										}}
										MenuProps={{
											PaperProps: {
												sx: {
													bgcolor: isDarkMode ? shades.secondary[700] : 'white',
												},
											},
										}}
									>
										<MenuItem
											value="all"
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											All
										</MenuItem>
										{drugCategories.map((category) => (
											<MenuItem
												key={category.id}
												value={category.id}
												sx={{
													fontSize: {
														xs: '0.7rem',
														sm: '0.75rem',
														md: '0.8rem',
													},
													color: isDarkMode ? shades.white[100] : 'inherit',
													'&:hover': {
														backgroundColor: isDarkMode
															? 'rgba(255,255,255,0.08)'
															: 'rgba(0,0,0,0.04)',
													},
													'&.Mui-selected': {
														backgroundColor: isDarkMode
															? 'rgba(144, 202, 249, 0.16)'
															: 'rgba(33, 150, 243, 0.08)',
														'&:hover': {
															backgroundColor: isDarkMode
																? 'rgba(144, 202, 249, 0.24)'
																: 'rgba(33, 150, 243, 0.12)',
														},
													},
												}}
											>
												{category.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{/* Proximity Filter */}
								<FormControl
									size="small"
									sx={{
										minWidth: '20%',
										maxWidth: '25%',
										backgroundColor: isDarkMode
											? shades.secondary[700]
											: 'white',
										'& .MuiOutlinedInput-root': {
											'& fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.2)'
													: '#2783f7',
											},
											'&:hover fieldset': {
												borderColor: isDarkMode
													? 'rgba(255,255,255,0.3)'
													: '#1976d2',
											},
											'&.Mui-focused fieldset': {
												borderColor: isDarkMode ? '#90caf9' : '#2783f7',
											},
										},
									}}
								>
									<InputLabel
										sx={{
											color: isDarkMode ? shades.white[200] : 'black',
											'&.Mui-focused': {
												color: isDarkMode ? '#90caf9' : 'black',
											},
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
										}}
									>
										Distance
									</InputLabel>
									<Select
										value={maxDistance}
										onChange={handleDistanceChange}
										label="Distance"
										sx={{
											color: isDarkMode ? shades.white[100] : '#2C3E50',
											fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
											'.MuiSelect-select': {
												padding: {
													xs: '6px 28px 6px 8px',
													sm: '8px 32px 8px 10px',
												},
											},
											'& .MuiSvgIcon-root': {
												color: isDarkMode ? shades.white[200] : 'inherit',
											},
										}}
										MenuProps={{
											PaperProps: {
												sx: {
													bgcolor: isDarkMode ? shades.secondary[700] : 'white',
												},
											},
										}}
									>
										<MenuItem
											value={0}
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											Any
										</MenuItem>
										<MenuItem
											value={5}
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											5 km
										</MenuItem>
										<MenuItem
											value={10}
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											10 km
										</MenuItem>
										<MenuItem
											value={20}
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											20 km
										</MenuItem>
										<MenuItem
											value={50}
											sx={{
												fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
												color: isDarkMode ? shades.white[100] : 'inherit',
											}}
										>
											50 km
										</MenuItem>
									</Select>
								</FormControl>
							</Box>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								gap: 3,
								paddingTop: 3,
							}}
						>
							{/* General Box for the Card Section */}
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									gap: 2,
									paddingLeft: { xs: 2.5, sm: 1 },
									width: '100%',
								}}
							>
								{/* Cards Section */}
								<Box sx={{ flexGrow: 1 }}>
									<Grid
										container
										spacing={3}
										sx={{
											minHeight: '100vh',
											position: 'relative',
										}}
									>
										{loading ? (
											[...Array(perPage)].map((_, index) => (
												<Grid item xs={6} sm={4} md={3} key={index}>
													<Skeleton
														variant="rectangular"
														width="100%"
														height={150}
													/>
												</Grid>
											))
										) : paginatedDrugs.length > 0 ? (
											paginatedDrugs.map((drug) => (
												<Grid item xs={12} sm={6} md={4} lg={3} key={drug.id}>
													{/* Card Component */}
													<Card
														sx={{
															height: { xs: '300px', sm: '420px', md: '460px' },
															width: '100%',
															maxWidth: {
																xs: '300px',
																sm: '400px',
																md: '450px',
															},
															backgroundColor: isDarkMode
																? shades.secondary[800]
																: 'white',
															display: 'flex',
															flexDirection: 'column',
															alignItems: 'center',
															margin: 'auto',
															position: 'relative',
															borderRadius: 2,
															border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
															boxShadow: isDarkMode
																? '0 4px 6px rgba(0,0,0,0.3)'
																: '0 4px 6px rgba(0,0,0,0.1)',
															transition: 'all 0.3s ease',
															'&:hover': {
																boxShadow: isDarkMode
																	? '0 8px 12px rgba(0,0,0,0.4)'
																	: '0 8px 12px rgba(0,0,0,0.2)',
																transform: 'translateY(-5px)',
															},
															flexShrink: 0,
														}}
													>
														<Box
															sx={{
																position: 'absolute',
																top: 10,
																right: 10,
																marginBottom: { xs: 2, sm: 3, md: 4 },
																padding: '4px 12px',
																borderRadius: '4px',
																display: 'inline-flex',
																alignItems: 'center',
																gap: 1,
																fontSize: {
																	xs: '0.6rem',
																	sm: '0.7rem',
																	md: '0.8rem',
																},
																fontWeight: 'medium',
																backgroundColor:
																	getAvailableStock(drug) === 0
																		? isDarkMode
																			? 'rgba(211, 47, 47, 0.8)'
																			: '#d32f2f'
																		: getAvailableStock(drug) <= 150
																			? isDarkMode
																				? 'rgba(255, 185, 33, 0.8)'
																				: '#FFB921'
																			: isDarkMode
																				? 'rgba(46, 125, 50, 0.8)'
																				: '#2e7d32',
																color: isDarkMode ? shades.white[100] : 'white',
																border: isDarkMode
																	? '1px solid rgba(255,255,255,0.1)'
																	: 'none',
																zIndex: 1,
																mb: { xs: '45px', sm: '55px', md: '65px' },
															}}
														>
															{getAvailableStock(drug) === 0 ? (
																<>
																	<CloseIcon sx={{ fontSize: 'inherit' }} /> Out
																	of Stock
																</>
															) : getAvailableStock(drug) <= 150 ? (
																<>
																	<WarningIcon sx={{ fontSize: 'inherit' }} />{' '}
																	Low Stock
																</>
															) : (
																<>
																	<CheckIcon sx={{ fontSize: 'inherit' }} /> In
																	Stock
																</>
															)}
														</Box>

														<Link
															to={`/app/products/${drug.id}`}
															style={{ textDecoration: 'none' }}
														>
															<Box
																sx={{
																	position: 'relative',
																	width: '100%',
																	height: { xs: 100, sm: 150, md: 200 },
																	overflow: 'hidden',
																	marginTop: {
																		xs: '35px',
																		sm: '45px',
																		md: '55px',
																	},
																}}
															>
																<CardMedia
																	component="img"
																	height="100%"
																	image={drug.img || '/default-image.jpg'}
																	alt={drug.name}
																	sx={{
																		objectFit: 'contain',
																		width: '80%',
																		height: '100%',
																		margin: '0 auto',
																	}}
																/>

																<Box
																	sx={{
																		position: 'absolute',
																		top: 0,
																		left: 0,
																		width: '100%',
																		height: '100%',
																		backgroundColor: 'rgba(0, 0, 0, 0.5)',
																		opacity: 0,
																		display: 'flex',
																		flexDirection: 'column',
																		justifyContent: 'center',
																		alignItems: 'center',
																	}}
																>
																	<Box
																		sx={{
																			display: 'flex',
																			gap: 1,
																			marginBottom: 2,
																		}}
																	></Box>
																</Box>
															</Box>
														</Link>
														<Box
															sx={{
																padding: 2,
																textAlign: 'center',
																flexGrow: 1,
															}}
														>
															<CardContent>
																<Typography
																	variant="h5"
																	sx={{
																		fontWeight: 'bold',
																		mb: 1.5,
																		color: isDarkMode
																			? shades.white[100]
																			: '#2C3E50',
																		fontSize: {
																			xs: '0.8rem',
																			sm: '1.0rem',
																			md: '1.2rem',
																		},
																		fontFamily: 'Inter, sans-serif',
																	}}
																>
																	{drug.name}
																</Typography>

																<Typography
																	variant="h6"
																	sx={{
																		fontSize: {
																			xs: '0.6rem',
																			sm: '0.8rem',
																			md: '1.1rem',
																		},
																		color: isDarkMode
																			? shades.neutral[300]
																			: 'inherit',
																	}}
																>
																	{drug.category}
																</Typography>
																<Typography
																	variant="h6"
																	sx={{
																		color: isDarkMode
																			? shades.accent[300]
																			: '#2C3E50',
																		fontWeight: 'bold',
																		mt: 'auto',
																		fontSize: {
																			xs: '0.7rem',
																			sm: '0.9rem',
																			md: '1.1rem',
																		},
																	}}
																>
																	{conversionLoading[drug.id] ? (
																		<span>Converting...</span>
																	) : (
																		formatPrice(
																			convertedPrices[drug.id] ||
																				drug.price_info.converted_price,
																			drug.price_info.user_currency,
																		)
																	)}
																</Typography>

																<Box
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		mb: 1.5,
																	}}
																>
																	<Rating
																		// name={`rating-${drug.id}`}
																		// value={rating}
																		precision={0.5}
																		// onChange={handleRatingChange}
																		// disabled={isLoading}
																		sx={{
																			'& .MuiRating-iconEmpty': {
																				color: isDarkMode
																					? '#ff80ab'
																					: '#ff4081',
																			},
																			'& .MuiRating-iconFilled': {
																				color: isDarkMode
																					? '#90caf9'
																					: '#2783f7',
																			},
																			'& .MuiRating-iconHover': {
																				color: isDarkMode
																					? '#64b5f6'
																					: '#1976d2',
																			},
																		}}
																	/>
																	<Typography
																		variant="body2"
																		sx={{
																			ml: 1,
																			color: isDarkMode
																				? shades.white[200]
																				: '#666',
																		}}
																	>
																		{/* ({totalRatings}) */}
																	</Typography>
																</Box>
																<Box>
																	<Button
																		variant="contained"
																		startIcon={<AddShoppingCartIcon />}
																		onClick={() => handleAddToCart(drug)}
																		disabled={drug.stock === 0}
																		sx={{
																			backgroundColor:
																				drug.stock === 0
																					? '#cccccc'
																					: isDarkMode
																						? '#1e88e5'
																						: '#2783f7',
																			fontSize: {
																				xs: '0.6rem',
																				sm: '0.8rem',
																				md: '0.9rem',
																			},
																			color: 'white',
																			padding: {
																				xs: '4px 8px',
																				sm: '6px 12px',
																				md: '8px 16px',
																			},
																			minWidth: {
																				xs: '100px',
																				sm: '120px',
																				md: '140px',
																			},
																			'&:hover': {
																				backgroundColor:
																					drug.stock === 0
																						? '#cccccc'
																						: isDarkMode
																							? '#0d47a1'
																							: '#1976d2',
																				transform:
																					drug.stock === 0
																						? 'none'
																						: 'translateY(-1px)',
																				cursor:
																					drug.stock === 0
																						? 'not-allowed'
																						: 'pointer',
																			},
																			'&:disabled': {
																				backgroundColor: isDarkMode
																					? '#424242'
																					: '#cccccc',
																				color: isDarkMode
																					? '#757575'
																					: '#666666',
																				filter: 'blur(0.5px)',
																				opacity: 0.7,
																				pointerEvents: 'none',
																			},
																			transition: 'all 0.2s ease',
																		}}
																	>
																		{drug.stock === 0
																			? 'Out of Stock'
																			: 'Add to Cart'}
																	</Button>

																	{cartError &&
																		cartError.drugId === drug.id && (
																			<Alert
																				severity="error"
																				sx={{
																					mt: 1,
																					fontSize: {
																						xs: '0.7rem',
																						sm: '0.8rem',
																					},
																					py: 0,
																					width: 'fit-content',
																				}}
																			>
																				{cartError.message}
																			</Alert>
																		)}
																</Box>

																<Box
																	sx={{
																		display: 'flex',
																		gap: 2,
																		alignItems: 'center',
																		mt: 2,
																		padding: 1,
																	}}
																></Box>
															</CardContent>
														</Box>
													</Card>
												</Grid>
											))
										) : (
											<Typography>No results found</Typography>
										)}
									</Grid>
									<Toolbar />
									{/* Pagination */}
									<Box
										sx={{
											width: '100%',
											position: 'relative',
											marginTop: 'auto',
											bottom: 0,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-end',
											flexDirection: 'row',
											backgroundColor: isDarkMode
												? shades.secondary[800]
												: 'white',
											padding: 2,
											zIndex: 100,
											borderTop: isDarkMode
												? '1px solid rgba(255,255,255,0.1)'
												: '1px solid rgba(0,0,0,0.1)',
										}}
									>
										<Pagination
											count={Math.ceil(filteredDrugs.length / itemsPerPage)}
											page={page}
											onChange={handlePageChange}
											variant="outlined"
											siblingCount={0}
											boundaryCount={0}
											hidePrevButton={false}
											hideNextButton={false}
											hideFirstButton={true}
											hideLastButton={true}
											sx={{
												'& .MuiPaginationItem-root': {
													color: isDarkMode ? shades.white[100] : 'black',
													border: isDarkMode
														? '1px solid rgba(255,255,255,0.3)'
														: '1px solid black',
													borderRadius: 4,
												},
												'& .MuiPaginationItem-ellipsis': {
													display: 'none',
												},
												'& .Mui-selected': {
													backgroundColor: isDarkMode
														? shades.accent[700]
														: 'black',
													color: isDarkMode ? shades.white[100] : '#FFB921',
													borderColor: 'transparent',
												},
												'& .MuiPaginationItem-root:hover': {
													backgroundColor: isDarkMode
														? 'rgba(255,255,255,0.2)'
														: 'black',
													color: 'white',
													borderColor: 'transparent',
												},
												'& .MuiPaginationItem-page:not(.Mui-selected)': {
													display: 'none',
												},
												'& .MuiSvgIcon-root': {
													color: isDarkMode ? shades.white[100] : 'inherit',
												},
											}}
										/>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default Listings;
